import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { TrendingUp } from "lucide-react";
import { EmptyState } from "./EmptyState";

export interface ChartPoint {
  label: string;
  value: number;
}

interface AdminChartCardProps {
  title: string;
  caption: string;
  empty: string;
  data: ChartPoint[];
  accentColor: string;
  gradientId: string;
  formatValue: (value: number) => string | number;
  variant?: "area" | "bar";
  headerAction?: React.ReactNode;
}

export function AdminChartCard({
  title,
  caption,
  empty,
  data,
  accentColor,
  gradientId,
  formatValue,
  variant = "area",
  headerAction,
}: AdminChartCardProps) {
  return (
    <section className="overview-panel overview-panel--chart">
      <header className="overview-panel__header">
        <span className="overview-panel__icon" aria-hidden>
          <TrendingUp className="h-4 w-4" strokeWidth={2.25} />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="overview-panel__title">{title}</h3>
          <p className="overview-panel__subtitle">{caption}</p>
        </div>
        {headerAction ? <div className="shrink-0">{headerAction}</div> : null}
      </header>

      <div className="overview-panel__body">
        {data.length === 0 ? (
          <EmptyState
            className="overview-panel-empty"
            icon={<TrendingUp className="h-5 w-5" />}
            title={empty}
            body="Data will appear once platform activity starts."
          />
        ) : (
          <div className="overview-chart">
            <ResponsiveContainer width="100%" height="100%">
              {variant === "bar" ? (
                <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: "var(--text-muted)" }}
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    angle={-18}
                    textAnchor="end"
                    height={52}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: "var(--text-muted)" }}
                    axisLine={false}
                    tickLine={false}
                    width={44}
                  />
                  <Tooltip
                    formatter={(value: number) => formatValue(value)}
                    contentStyle={{
                      borderRadius: 12,
                      border: "1px solid var(--border-subtle)",
                      boxShadow: "var(--shadow-raised)",
                    }}
                  />
                  <Bar dataKey="value" fill={accentColor} radius={[8, 8, 0, 0]} />
                </BarChart>
              ) : (
                <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={accentColor} stopOpacity={0.28} />
                      <stop offset="100%" stopColor={accentColor} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 12, fill: "var(--text-muted)" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: "var(--text-muted)" }}
                    axisLine={false}
                    tickLine={false}
                    width={44}
                  />
                  <Tooltip
                    formatter={(value: number) => formatValue(value)}
                    contentStyle={{
                      borderRadius: 12,
                      border: "1px solid var(--border-subtle)",
                      boxShadow: "var(--shadow-raised)",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke={accentColor}
                    strokeWidth={2.75}
                    fill={`url(#${gradientId})`}
                    animationDuration={900}
                  />
                </AreaChart>
              )}
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </section>
  );
}
